import { useRouteError, useNavigate } from "react-router-dom";
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AppErrorBoundary() {
  const error: any = useRouteError();
  const navigate = useNavigate();

  console.error("[APP_ROUTER_CATASTROPHIC_ERROR]", error);

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6 relative overflow-hidden select-none">
      {/* Decorative Gradients */}
      <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-indigo-500/10 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 w-[400px] h-[400px] rounded-full bg-rose-500/10 blur-[100px] pointer-events-none" />

      {/* Main Glassmorphic Container */}
      <div className="w-full max-w-lg bg-slate-900/60 backdrop-blur-xl border border-slate-800/80 p-8 sm:p-10 rounded-3xl shadow-2xl flex flex-col items-center text-center space-y-8 animate-in fade-in zoom-in-95 duration-500 relative z-10">
        
        {/* Floating Animated Icon Wrapper */}
        <div className="w-20 h-20 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-500 shadow-lg shadow-rose-500/5 animate-pulse">
          <AlertTriangle className="w-10 h-10" />
        </div>

        {/* Text Header */}
        <div className="space-y-3">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight bg-gradient-to-r from-slate-100 via-slate-300 to-slate-100 bg-clip-text text-transparent">
            Opa! Algo deu errado.
          </h1>
          <p className="text-sm text-slate-400 max-w-sm mx-auto leading-relaxed">
            Ocorreu uma falha inesperada na renderização desta página. Mas não se preocupe, seus dados de progresso estão seguros.
          </p>
        </div>

        {/* Error Detail Box (Only shown in non-production or for debugging) */}
        {error && (
          <div className="w-full text-left bg-slate-950/80 border border-slate-800/50 p-4 rounded-xl font-mono text-xs text-rose-300/80 max-h-32 overflow-y-auto whitespace-pre-wrap leading-relaxed shadow-inner">
            <span className="font-bold text-rose-400 block mb-1">Detalhes do erro:</span>
            {error.message || String(error)}
          </div>
        )}

        {/* Action Buttons */}
        <div className="w-full flex flex-col sm:flex-row gap-4 pt-2">
          <Button
            onClick={handleReload}
            className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl py-6 font-bold shadow-md shadow-indigo-600/10 hover:shadow-indigo-600/20 transition-all duration-300 flex items-center justify-center gap-2 group"
          >
            <RefreshCw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" />
            Recarregar Página
          </Button>

          <Button
            onClick={handleGoHome}
            variant="outline"
            className="flex-1 border-slate-800 hover:border-slate-700 bg-slate-900/40 text-slate-300 hover:text-slate-100 hover:bg-slate-800/40 rounded-xl py-6 font-bold transition-all duration-300 flex items-center justify-center gap-2"
          >
            <Home className="w-4 h-4" />
            Painel Principal
          </Button>
        </div>

        {/* Back Link */}
        <button
          onClick={() => navigate(-1)}
          className="text-xs text-slate-500 hover:text-slate-300 transition-colors flex items-center gap-1.5 pt-2"
        >
          <ArrowLeft className="w-3 h-3" />
          Voltar para a página anterior
        </button>
      </div>
    </div>
  );
}
